/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
// code-reaper-ignore-file -- Analytics implementations for the browser.

import React from 'react';
import {
	fireTrackAnalyticsDeferred as browserFireTrackAnalyticsDeferred,
	fireOperationalAnalyticsDeferred as browserFireOperationalAnalyticsDeferred,
	fireScreenAnalyticsDeferred as browserFireScreenAnalyticsDeferred,
} from './utils/fire-analytics.tsx';
import { MountEvent as BrowserMountEvent } from './utils/mount-event-component.tsx';
import { MountEvent as ServerMountEvent } from './server.tsx';

// Deferred fire functions — wrapped with __SERVER__ guard because fireEventWhenIdle accesses window
/* eslint-disable jira/jira-ssr/no-server-variable */
export const fireTrackAnalyticsDeferred: typeof browserFireTrackAnalyticsDeferred = (
	event,
	...rest
) => {
	if (__SERVER__) return;
	return browserFireTrackAnalyticsDeferred(event, ...rest);
};
export const fireOperationalAnalyticsDeferred: typeof browserFireOperationalAnalyticsDeferred = (
	event,
	...rest
) => {
	if (__SERVER__) return;
	return browserFireOperationalAnalyticsDeferred(event, ...rest);
};
export const fireScreenAnalyticsDeferred: typeof browserFireScreenAnalyticsDeferred = (...args) => {
	if (__SERVER__) return;
	return browserFireScreenAnalyticsDeferred(...args);
};
/* eslint-enable jira/jira-ssr/no-server-variable */

// Fire functions — direct re-exports (fireAnalytics already guards with __SERVER__)
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export {
	fireAnalytics,
	fireOperationalAnalytics,
	fireScreenAnalytics,
	fireTrackAnalytics,
	fireUIAnalytics,
} from './utils/fire-analytics';
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { getEvent } from './utils/get-event-for-web-client';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export {
	SCREEN_EVENT_TYPE,
	TRACK_EVENT_TYPE,
	OPERATIONAL_EVENT_TYPE,
	UI_EVENT_TYPE,
	type EventTypes,
	DRAWER,
	DROPDOWN,
	INLINE_DIALOG,
	MODAL,
	BANNER,
	SCREEN,
	type Screens,
} from './constants';
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { ContextualAnalyticsData } from './utils/analytics-context-wrapper';
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type { AnalyticsContextData } from './utils/analytics-context-wrapper';
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { AnalyticsEventToProps } from './utils/analytics-event-to-props';
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export {
	type AnalyticsProps,
	WithAnalytics,
	FireOperationalAnalytics,
	FireScreenAnalytics,
	FireTrackAnalytics,
	FireUiAnalytics,
} from './utils/fire-analytics-component';

export const MountEvent: typeof ServerMountEvent = (props) => {
	if (__SERVER__) {
		return <ServerMountEvent onMount={props.onMount} />;
	}
	// Note: This is a workaround to avoid type errors. The type of the props is not the same as the type of the BrowserMountEvent.
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any, typescript/consistent-type-assertions
	return <BrowserMountEvent {...(props as any)} />;
};
// Preserve the original displayName so snapshot tests that serialise the element tree continue to match.
MountEvent.displayName = BrowserMountEvent.displayName;

// Type re-exports
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type { RestArguments, ActionSubjectAndAction } from './utils/extract-data-from-event';
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type {
	AnalyticsAttributes,
	AnalyticsAttributesUntyped as Attributes,
	AnalyticsEvent,
} from './types';
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type { default as UIAnalyticsEvent } from '@atlaskit/analytics-next/UIAnalyticsEvent';
